import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, ChevronLeft, Heart, HeartHandshake, Languages } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'ar', label: 'ع' }
] as const;

interface NavSubItem {
  title: {
    ar: string;
    en: string;
  };
  hidden?: boolean;
}

interface NavItem {
  id?: string;
  title: {
    ar: string;
    en: string;
  };
  dropdown: boolean;
  items?: NavSubItem[];
  order: number;
  hidden?: boolean;
}

// Helper function to generate route from title
const generateRoute = (title: string): string => {
  // Special case for "الرئيسية" or "Home" - go to /landing
  if (title === 'الرئيسية' || title === 'Home') {
    return '/landing';
  }
  
  // Remove Arabic diacritics and normalize
  const normalized = String(title).trim().toLowerCase();
  // For Arabic text, use transliteration or slug
  const slug = normalized
    .replace(/\s+/g, '-')
    .replace(/[^\w\u0600-\u06FF-]/g, ''); // Keep Arabic characters and hyphens
  return `/landing/${encodeURIComponent(slug)}`;
};

export function LandingNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<number | null>(null);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { language, setLanguage } = useLanguage();

  const selectedLanguage = languages.find(l => l.code === language);
  const { t } = useLanguage();

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  useEffect(() => {
    fetchNavItems();
    
    // Cleanup timeout on unmount
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const fetchNavItems = async () => {
    try {
      const navQuery = query(collection(db, 'landing_navigation'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(navQuery);

      const items = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as NavItem[];

      // Filter out hidden items and hidden sub-items
      const visibleItems = items
        .filter(item => !item.hidden)
        .map(item => ({
          ...item,
          items: item.items?.filter(subItem => !subItem.hidden)
        }));

      setNavItems(visibleItems);
    } catch (error) {
      console.error('Error fetching navigation items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = (index: number) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(index);
  };

  const handleMouseLeave = () => {
    // Add delay before closing the dropdown
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay
  };

  if (loading) {
    return (
      <nav className="bg-[#004250] text-white fixed w-full z-50 top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-shrink-0">
              <a href="https://namat.org.sa">
                <img src="/NamatLogo.png" alt={t('landing.nav.logoAlt')} className="h-14 w-auto" />
              </a>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-[#004250] text-white fixed w-full z-50 top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <div className="flex-shrink-0">
            <a href="https://namat.org.sa">
              <img src="/NamatLogo.png" alt={t('landing.nav.logoAlt')} className="h-14 w-auto" />
            </a>
          </div>
          
          <div className="hidden md:flex flex-grow items-center justify-center">
            <div className={`flex justify-center items-baseline max-w-3xl ${language === 'ar' ? 'gap-3 mr-12' : 'gap-2 ml-12'}`}>
              {navItems.map((item, index) => {
                const itemTitle = item.title[language] || item.title.ar;
                
                return (
                <div 
                  key={item.id || index} 
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <a
                    href={generateRoute(itemTitle)}
                    className="hover:bg-[#7dda55] px-3 py-2 rounded-full text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                  >
                    {itemTitle}
                    {item.dropdown && <ChevronDown className="h-4 w-4" />}
                  </a>
                  {item.dropdown && activeDropdown === index && (
                    <div 
                      className={`absolute ${language === 'ar' ? 'right-0' : 'left-0'} mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5`}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="py-2" role="menu">
                        {item.items?.map((subItem, subIndex) => {
                          const subItemTitle = subItem.title[language] || subItem.title.ar;
                          
                          return (
                          <a
                            key={subIndex}
                            href={generateRoute(subItemTitle)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#004250]"
                            role="menuitem"
                          >
                            {subItemTitle}
                          </a>
                        )})}
                      </div>
                    </div>
                  )}
                </div>
              )})}
            </div>
          </div>

          <div className="hidden md:flex flex-shrink-0 gap-3">
            <button
              onClick={toggleLanguage}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Languages className="w-4 h-4" />
              {selectedLanguage?.label}
            </button>
            <Link
              to="/request-treatment"
              className="bg-white hover:bg-gray-100 text-[#004250] px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
            >
              <HeartHandshake className="w-4 h-4" />
              {t('landing.nav.requestTreatment')}
            </Link>
            <a
              href="https://donate.namat.org.sa"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#7dda55] hover:bg-[#6bc748] text-white px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Heart className="w-4 h-4" />
              {t('landing.nav.donate')}
            </a>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <Link
              to="/request-treatment"
              className="bg-white hover:bg-gray-100 text-[#004250] px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
            >
              <HeartHandshake className="w-4 h-4" />
              {t('landing.nav.requestTreatment')}
            </Link>
            <a
              href="https://donate.namat.org.sa"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#7dda55] hover:bg-[#6bc748] text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap"
            >
              <Heart className="w-4 h-4" />
              {t('landing.nav.donate')}
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-[#7dda55] focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 top-24 bg-[#004250] overflow-y-auto">
          <div className="px-2 pt-2 pb-20 space-y-1 sm:px-3">
            <button
              onClick={() => { toggleLanguage(); setIsOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium hover:bg-[#7dda55] transition-colors border-b border-white/10 mb-2 pb-3"
            >
              <span className="flex items-center gap-2"><Languages className="h-5 w-5" />{language === 'ar' ? 'English' : 'العربية'}</span>
              <span className="text-xs text-white/70">{selectedLanguage?.label}</span>
            </button>
            {navItems.map((item, index) => {
              const itemTitle = item.title[language] || item.title.ar;
              
              return (
              <div key={item.id || index}>
                {item.dropdown ? (
                  <button
                    onClick={() => setActiveMobileDropdown(activeMobileDropdown === index ? null : index)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium hover:bg-[#7dda55] transition-colors"
                  >
                    <span>{itemTitle}</span>
                    <ChevronLeft className={`h-5 w-5 transition-transform ${activeMobileDropdown === index ? 'rotate-90' : ''}`} />
                  </button>
                ) : (
                  <a
                    href={generateRoute(itemTitle)}
                    className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#7dda55] transition-colors"
                  >
                    {itemTitle}
                  </a>
                )}
                {item.dropdown && activeMobileDropdown === index && (
                  <div className="pr-4 space-y-1 mt-1">
                    {item.items?.map((subItem, subIndex) => {
                      const subItemTitle = subItem.title[language] || subItem.title.ar;
                      
                      return (
                      <a
                        key={subIndex}
                        href={generateRoute(subItemTitle)}
                        className="block px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-[#7dda55]/50 rounded-md"
                      >
                        {subItemTitle}
                      </a>
                    )})}
                  </div>
                )}
              </div>
            )})}
          </div>
        </div>
      )}
    </nav>
  );
}

